import React from 'react';
import clsx from 'clsx';

/**
 * Tags citation snippets may contain. Document chunks are HTML (often tables);
 * anything outside this set is stripped before render.
 */
const ALLOWED_TAGS = new Set([
  'TABLE',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TD',
  'TH',
  'CAPTION',
  'COLGROUP',
  'COL',
  'P',
  'DIV',
  'SPAN',
  'BR',
  'HR',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'SUB',
  'SUP',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'PRE',
  'CODE',
  'MARK'
]);

/**
 * Tags whose subtree is discarded rather than unwrapped.
 *
 * Two reasons. Unwrapping `<script>` or `<style>` would promote its source into
 * the card as visible text — inert, but it reads as a broken snippet. And the
 * foreign-content and raw-text elements here are the ones that parse differently
 * depending on whether scripting is enabled, which is the lever mutation-XSS
 * pulls: the fragment is parsed once to sanitize it and again by the browser
 * when it is inserted, and the two parses must not disagree. Dropping them
 * outright means there is nothing to disagree about.
 */
const DROPPED_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'NOSCRIPT',
  'SVG',
  'MATH',
  'IFRAME',
  'FRAME',
  'FRAMESET',
  'OBJECT',
  'EMBED',
  'APPLET',
  'LINK',
  'META',
  'BASE',
  'HEAD',
  'TITLE',
  'FORM',
  'INPUT',
  'BUTTON',
  'SELECT',
  'TEXTAREA',
  'OPTION',
  'AUDIO',
  'VIDEO',
  'CANVAS',
  'PLAINTEXT',
  'XMP'
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  MARK: new Set(['class']),
  TD: new Set(['colspan', 'rowspan']),
  TH: new Set(['colspan', 'rowspan']),
  COL: new Set(['span'])
};

/** A [[label]] in the email template preview, which marks it after reading the body. */
export const TEMPLATE_FIELD_MARK_CLASS = 'template-field';

/** Only the highlight classes this app inserts are kept on <mark>. */
const ALLOWED_MARK_CLASSES = new Set(['citation-quote-highlight', TEMPLATE_FIELD_MARK_CLASS]);

const sanitizeAttributes = (el: Element) => {
  const allowed = ALLOWED_ATTRS[el.tagName];
  for (const attr of [...el.attributes]) {
    if (!allowed?.has(attr.name.toLowerCase())) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (el.tagName === 'MARK' && attr.name.toLowerCase() === 'class') {
      const keep = attr.value
        .split(/\s+/)
        .filter((c) => ALLOWED_MARK_CLASSES.has(c))
        .join(' ');
      if (keep) {
        el.setAttribute('class', keep);
      } else {
        el.removeAttribute('class');
      }
    }
  }
};

/**
 * Reduce an HTML fragment to the tags and attributes a citation snippet needs.
 *
 * The snippet is server-built, but its content is OCR text from documents an
 * agency uploaded, so this is a real boundary rather than a formality: whatever
 * a PDF happens to contain reaches it. The allowlist carries no `href`, `src`,
 * `style`, or event handler, so no attribute survives that could load or run
 * anything.
 *
 * Browser-only (DOMParser); citation hover cards never SSR.
 */
export const sanitizeCitationHtml = (html: string): string => {
  if (!html || typeof DOMParser === 'undefined') {
    return '';
  }
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) {
    return '';
  }

  // Walked live rather than over a snapshot of the child list: unwrapping lifts
  // an element's children into the list being iterated, and those children have
  // not been checked yet. Resuming at the first promoted node is what stops a
  // disallowed element nested inside another one from reaching the output.
  const walk = (node: Node) => {
    let child = node.firstChild;
    while (child) {
      const next = child.nextSibling;

      if (child.nodeType === Node.COMMENT_NODE) {
        node.removeChild(child);
        child = next;
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child = next;
        continue;
      }

      const el = child as Element;
      if (DROPPED_TAGS.has(el.tagName)) {
        node.removeChild(el);
        child = next;
        continue;
      }
      if (!ALLOWED_TAGS.has(el.tagName)) {
        // Unwrap: an unknown wrapper is usually just structure the document
        // carried, and its text is the part worth showing.
        const promoted = el.firstChild;
        while (el.firstChild) {
          node.insertBefore(el.firstChild, el);
        }
        node.removeChild(el);
        child = promoted ?? next;
        continue;
      }

      sanitizeAttributes(el);
      walk(el);
      child = next;
    }
  };
  walk(root);
  return root.innerHTML;
};

type SanitizedHtmlProps = {
  html: string;
  className?: string;
};

/**
 * Renders a trusted-then-sanitized HTML fragment. Use for document snippets
 * (tables, inline markup) — not for markdown; use {@link SanitizedMarkdown} there.
 */
export const SanitizedHtml = (props: SanitizedHtmlProps) => {
  const sanitized = React.useMemo(() => sanitizeCitationHtml(props.html), [props.html]);
  return <div className={clsx(props.className)} dangerouslySetInnerHTML={{ __html: sanitized }} />;
};
